import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectCompact } from 'app/pages/Home/slice/selectors';
import { computeTimelineBodyWidthPx } from '../utils/computeTimelineBodyWidthPx';
import {
  sumMonthSectionWidths,
  useMonthSectionWidths
} from '../utils/useMonthSectionWidths';
import { MonthlyTimelineGridLineFiller } from './MonthlyTimelineGridLineFiller';
import { MonthlyTimelineRow } from './MonthlyTimelineRow';
import type {
  DayColumnDescriptor,
  MonthDescriptor,
  MonthlyTimelineLane
} from '../utils/types';
import roadmapStyles from 'app/components/RoadmapCard/Roadmap.module.scss';

interface MonthlySwimlaneGridProps {
  months: MonthDescriptor[];
  lanes: MonthlyTimelineLane[];
  expandedMonths: string[];
  dayColumns: DayColumnDescriptor[];
  getDayLeftPx: (monthKey: string, day: number) => number | null;
  totalColumns: number;
  gapWidth: number;
}

export const MonthlySwimlaneGrid = memo(
  ({
    months,
    lanes,
    expandedMonths,
    dayColumns,
    getDayLeftPx,
    totalColumns,
    gapWidth
  }: MonthlySwimlaneGridProps) => {
    const isCompact = useSelector(selectCompact);

    const monthSections = useMonthSectionWidths(
      months,
      expandedMonths,
      isCompact
    );
    const columnsWidthPx = sumMonthSectionWidths(monthSections);

    const bodyWidthPx = useMemo(
      () =>
        computeTimelineBodyWidthPx(lanes, months, getDayLeftPx, totalColumns),
      [lanes, months, getDayLeftPx, totalColumns]
    );

    const bodyExtensionPx = Math.max(0, bodyWidthPx - columnsWidthPx);

    return (
      <div className={roadmapStyles.rowsWrapper}>
        <div className={roadmapStyles.timelineRows}>
          {lanes.map(lane => (
            <MonthlyTimelineRow
              key={lane.key}
              lane={lane}
              months={months}
              expandedMonths={expandedMonths}
              dayColumns={dayColumns}
              getDayLeftPx={getDayLeftPx}
              bodyWidthPx={bodyWidthPx}
              columnsWidthPx={columnsWidthPx}
              gapWidth={gapWidth}
            />
          ))}

          <MonthlyTimelineGridLineFiller
            monthSections={monthSections}
            bodyExtensionPx={bodyExtensionPx}
            gapWidth={gapWidth}
          />
        </div>
      </div>
    );
  }
);
